import { ArrowLeft, ArrowRight, Award, X } from 'lucide-react';
import { useState, useEffect } from 'react';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useToast } from '@/hooks/use-toast';
import { useTranslations } from '@/hooks/use-translations';
import { supabase } from '@/integrations/supabase/client';
import { useCasesConfig } from '@/modules/case/hooks/useCasesConfig';
import { useCaseDetails } from '@/modules/case-selector/hooks/useCaseDetails';
import { trackEvent } from '@/modules/google-analytics/analytic/analytics';
import { MasterclassRatingModal } from '@/modules/masterclass-rating';
import { captureSentry } from '@/utils/sentryHelper';

import { CertificationResultModal } from './CertificationResultModal';
import { CERTIFICATION_CONFIG } from '../constants';
import { CertificationQuestion } from '../types';

interface CertificationTestProps {
  caseId: string;
  userId: string;
  onClose: () => void;
  onTestComplete?: () => void;
}

export const CertificationTest = ({
  caseId,
  userId,
  onClose,
  onTestComplete,
}: CertificationTestProps) => {
  const { t } = useTranslations();
  const { getCaseMetadata, casesConfig } = useCasesConfig();
  const caseItem = casesConfig.cases.find((c) => c.id === caseId)!;

  const { details: caseDetails, loading: caseDetailsLoading } = useCaseDetails(caseItem.configUrl);
  const [questions, setQuestions] = useState<CertificationQuestion[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<(number | null)[]>([]);
  const [attemptId, setAttemptId] = useState<string | null>(null);
  const [loadingCertification, setLoadingCertification] = useState(true);
  const [showResults, setShowResults] = useState(false);
  const [showRating, setShowRating] = useState(false);
  const [score, setScore] = useState(0);
  const [passed, setPassed] = useState(false);
  const [remainingAttempts, setRemainingAttempts] = useState(0);
  const { toast } = useToast();

  const isLoading = loadingCertification || caseDetailsLoading;

  useEffect(() => {
    if (casesConfig) {
      void loadCertification();
    }
  }, [caseId, userId, casesConfig]);

  /**
   * Loads certification questions from external API.
   *
   * Data Source:
   * - Questions are fetched from `certificationUrl` defined in case-config.json
   * - URL format: https://api.livelinx.pro/patientCases/certifications/{caseKey}_certification.json
   *
   * Expected JSON Structure:
   * {
   *   "case_id": number,
   *   "case_key": string,
   *   "certification_questions": [
   *     {
   *       "id": string,
   *       "question_text": string,
   *       "options": string[],
   *       "correct_answer": number,
   *       "explanation": string
   *     }
   *   ],
   *   "passing_score": number
   * }
   *
   * @throws {Error} If certificationUrl is not found in case metadata
   * @throws {Error} If API request fails
   */
  const loadCertification = async () => {
    try {
      // Load questions from external API
      const caseMetadata = getCaseMetadata(caseId);

      if (!caseMetadata) {
        console.error('Case metadata not loaded yet');
        return;
      }

      if (!caseMetadata.certificationUrl) {
        throw new Error(`No certification URL found for case ${caseId}`);
      }

      const response = await fetch(caseMetadata.certificationUrl);

      if (!response.ok) {
        throw new Error(`Failed to load certification: ${response.statusText}`);
      }

      const certificationData = await response.json();

      const formattedQuestions = certificationData.certification_questions.map((q: any) => ({
        id: q.id,
        case_id: certificationData.case_id,
        question_text: q.question_text,
        options: q.options,
        correct_answer: q.correct_answer,
        explanation: q.explanation,
      }));

      setQuestions(formattedQuestions);
      setAnswers(new Array(formattedQuestions.length).fill(null));

      // Check for existing in-progress attempt
      const { data: progressData } = await supabase
        .from('certification_progress')
        .select('*')
        .eq('user_id', userId)
        .eq('case_id', caseId)
        .is('completed_at', null)
        .maybeSingle();

      if (progressData) {
        // Restore existing progress
        setAttemptId(progressData.id);
        setCurrentQuestionIndex(progressData.current_question ?? 0);
        const savedAnswers = Array.isArray(progressData.answers)
          ? progressData.answers.map((a) => (typeof a === 'number' ? a : null))
          : new Array(formattedQuestions.length).fill(null);
        setAnswers(savedAnswers);
      } else {
        // Calculate next attempt number
        const { data: completedAttempts } = await supabase
          .from('certification_progress')
          .select('attempt_number')
          .eq('user_id', userId)
          .eq('case_id', caseId)
          .not('completed_at', 'is', null)
          .order('attempt_number', { ascending: false })
          .limit(1);

        const nextAttemptNumber = (completedAttempts?.[0]?.attempt_number || 0) + 1;

        // Create new in-progress attempt
        const { data: newProgress } = await supabase
          .from('certification_progress')
          .insert({
            user_id: userId,
            case_id: caseId,
            attempt_number: nextAttemptNumber,
            current_question: 0,
            answers: [],
          })
          .select()
          .single();

        if (newProgress) {
          setAttemptId(newProgress.id);
        }
      }
    } catch (error) {
      console.error('Error loading certification:', error);
      captureSentry({
        title: 'Failed to load certification',
        error,
        detail: { caseId, userId },
      });
      toast({
        title: 'Error',
        description: t('validation.errors.loadingCertification'),
        variant: 'destructive',
      });
    } finally {
      setLoadingCertification(false);
    }
  };

  const saveProgress = async (questionIndex: number, newAnswers: (number | null)[]) => {
    if (!attemptId) return;

    await supabase
      .from('certification_progress')
      .update({
        current_question: questionIndex,
        answers: newAnswers,
      })
      .eq('id', attemptId);
  };

  const handleAnswerSelect = (answerIndex: number) => {
    const newAnswers = [...answers];
    newAnswers[currentQuestionIndex] = answerIndex;
    setAnswers(newAnswers);
    void saveProgress(currentQuestionIndex, newAnswers);
  };

  const handleNext = () => {
    if (currentQuestionIndex < questions.length - 1) {
      const nextIndex = currentQuestionIndex + 1;
      setCurrentQuestionIndex(nextIndex);
      void saveProgress(nextIndex, answers);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      const prevIndex = currentQuestionIndex - 1;
      setCurrentQuestionIndex(prevIndex);
      void saveProgress(prevIndex, answers);
    }
  };

  const handleSubmit = async () => {
    if (!attemptId) return;

    try {
      // Calculate score
      const correctAnswers = answers.filter(
        (answer, index) => answer === questions[index].correct_answer
      ).length;

      const finalScore = (correctAnswers / questions.length) * 100;
      const testPassed = finalScore >= CERTIFICATION_CONFIG.PASSING_SCORE_CERTIFICATION;

      trackEvent('Certification', 'Finish Certification', caseId, Math.round(finalScore));

      // Mark the attempt as completed
      const { error: updateError } = await supabase
        .from('certification_progress')
        .update({
          score: finalScore,
          passed: testPassed,
          completed_at: new Date().toISOString(),
        })
        .eq('id', attemptId);

      if (updateError) throw updateError;

      // Calculate remaining attempts for the failure modal
      const { data: allAttempts } = await supabase
        .from('certification_progress')
        .select('attempt_number')
        .eq('user_id', userId)
        .eq('case_id', caseId)
        .not('completed_at', 'is', null);

      const attemptsUsed = allAttempts?.length || 0;
      const remaining = Math.max(0, CERTIFICATION_CONFIG.MAX_ATTEMPTS - attemptsUsed);

      // Check if user has already rated this masterclass (regardless of attempt)
      const { data: existingRating } = await supabase
        .from('masterclass_ratings')
        .select('id')
        .eq('user_id', userId)
        .eq('case_id', caseId)
        .maybeSingle();

      setScore(finalScore);
      setPassed(testPassed);
      setRemainingAttempts(remaining);

      // Show rating if not already done, otherwise show results
      if (!existingRating) {
        setShowRating(true);
      } else {
        setShowResults(true);
      }
    } catch (error) {
      console.error('Error submitting certification:', error);
      captureSentry({
        title: 'Failed to submit certification',
        error,
        detail: { caseId, userId, attemptId },
      });
      toast({
        title: 'Error',
        description: 'Unable to submit certification',
        variant: 'destructive',
      });
    }
  };

  const handleCloseResults = () => {
    setShowResults(false);
    if (onTestComplete) onTestComplete();
    onClose();
  };

  if (isLoading || !caseDetails) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        <Card className="p-8">
          <p className="font-body text-lg">{t('certification.test.loadingTest')}</p>
        </Card>
      </div>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];
  const progress = ((currentQuestionIndex + 1) / questions.length) * 100;
  const allAnswered = answers.every((a) => a !== null);

  return (
    <>
      {!showRating && !showResults && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <Card className="max-h-[90vh] w-full max-w-3xl overflow-y-auto p-6">
            <div className="mb-6 flex items-center justify-between">
              <h2 className="font-heading text-3xl text-livelinx-purple200">
                <Award className="mr-2 inline h-8 w-8" />
                {t('certification.test.title', {
                  caseTitle: caseDetails.displayName,
                })}
              </h2>
              <Button variant="ghost" onClick={onClose} size="icon">
                <X className="h-5 w-5" />
              </Button>
            </div>

            <div className="mb-6">
              <div className="mb-2 flex justify-between text-sm text-livelinx-grey200">
                <span>
                  {t('certification.test.questionProgress', {
                    current: currentQuestionIndex + 1,
                    total: questions.length,
                  })}
                </span>
                <span>
                  {t('certification.test.progressComplete', {
                    percent: progress.toFixed(0),
                  })}
                </span>
              </div>
              <Progress value={progress} className="h-2" />
            </div>

            <div className="mb-8">
              <h3 className="mb-4 font-body text-xl text-livelinx-text">
                {currentQuestion?.question_text}
              </h3>

              <RadioGroup
                value={answers[currentQuestionIndex]?.toString() || ''}
                onValueChange={(value) => handleAnswerSelect(parseInt(value))}
              >
                {currentQuestion?.options.map((option, index) => (
                  <div
                    key={index}
                    className="mb-3 flex items-center space-x-3 rounded-lg border border-livelinx-lightgray100 p-4 transition-colors hover:bg-livelinx-purple50"
                  >
                    <RadioGroupItem value={index.toString()} id={`option-${index}`} />
                    <Label
                      htmlFor={`option-${index}`}
                      className="flex-1 cursor-pointer font-body text-base"
                    >
                      {option}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>

            <div className="flex items-center justify-between border-t border-livelinx-lightgray100 pt-4">
              <Button
                onClick={handlePrevious}
                disabled={currentQuestionIndex === 0}
                variant="outline"
                className="border-livelinx-purple200 text-livelinx-purple200"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                {t('common.buttons.previous')}
              </Button>

              {currentQuestionIndex === questions.length - 1 ? (
                <Button
                  onClick={() => void handleSubmit()}
                  disabled={!allAnswered}
                  className="bg-livelinx-purple200 text-white hover:bg-livelinx-purple400"
                >
                  {t('certification.test.submitTest')}
                  <Award className="ml-2 h-4 w-4" />
                </Button>
              ) : (
                <Button
                  onClick={handleNext}
                  disabled={
                    answers[currentQuestionIndex] === null ||
                    answers[currentQuestionIndex] === undefined
                  }
                  className="bg-livelinx-purple200 text-white hover:bg-livelinx-purple400"
                >
                  {t('common.buttons.next')}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              )}
            </div>
          </Card>
        </div>
      )}

      {showRating && attemptId && (
        <MasterclassRatingModal
          caseId={caseId}
          userId={userId}
          certificationProgressId={attemptId}
          isOpen={showRating}
          onRatingSubmitted={() => {
            setShowRating(false);
            setShowResults(true);
          }}
        />
      )}

      {showResults && (
        <CertificationResultModal
          score={score}
          passed={passed}
          caseId={caseId}
          caseTitle={caseDetails.displayName}
          onClose={handleCloseResults}
          remainingAttempts={remainingAttempts}
        />
      )}
    </>
  );
};
